import {Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject} from '@angular/core';
import {DialogService} from "../../../shared/services/dialog.service";
import {DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";

import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {TYPE} from "../../../shared/types/types";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {FormInputComponent} from "../../../shared/elements/form-input/form-input.component";
import {FormBuilder, FormGroup} from "@angular/forms";
import {LanguageService} from "../../../shared/services/language.service";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {DocumentService} from "../../../shared/services/document.service";
import {ItemDocumentsPreviewSelectedComponent} from "../item-documents-preview-selected/item-documents-preview-selected.component";
import {ItemDocumentsFolderComponent} from "../item-documents-folder/item-documents-folder.component";
import {ItemDocumentsPreviewAvailableComponent} from "../item-documents-preview-available/item-documents-preview-available.component";
import {ApiService} from '../../../shared/services/api.service';
import {StorageFilesystemResource} from '../../../shared/types/settings-types/storageFilesystemResource';
import {storageEntryToDocument} from '../../../shared/mapper/file-interop';
import {ToasterService} from '../../../shared/services/toaster.service';
import {SettingsMenuItemType} from '../../../shared/types/settings-types/settingsMenuItemType';
import {take} from 'rxjs';
import {StorageModule} from '../../../shared/types/settings-types/storageModule';
import {StorageFilesystemGet200Response} from '../../../shared/types/settings-types/storageFilesystemGet200Response';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {startWith} from 'rxjs/operators';

@Component({
    selector: 'app-dialog-documents-commander',
    imports: [
        DialogModule,
        HeadlineComponent,
        TooltipComponent,
        FormButtonComponent,
        TooltipModule,
        TranslatePipe,
        FormInputComponent,
        ContentComponent,
        ItemDocumentsPreviewSelectedComponent,
        ItemDocumentsFolderComponent,
        ItemDocumentsPreviewAvailableComponent
    ],
    templateUrl: './dialog-documents-commander.component.html',
    styleUrl: './dialog-documents-commander.component.scss'
})
export class DialogDocumentsCommanderComponent implements OnInit {
  public dialogService: DialogService = inject(DialogService);
  public documentService: DocumentService = inject(DocumentService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private languageService: LanguageService = inject(LanguageService);
  private api: ApiService = inject(ApiService);
  private toaster: ToasterService = inject(ToasterService);
  private destroyRef: DestroyRef = inject(DestroyRef);

  @Input() public visible: boolean = false;
  @Input() public files: TYPE.Document[] = [];
  @Input() public view: 'dialog' | 'component' = 'dialog';
  @Input() public type: 'image' | 'media' | 'attachment' | 'both' = 'both';
  @Input() public templateModule: SettingsMenuItemType | '' = '';
  @Output() public  visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public  filesChange: EventEmitter<TYPE.Document[]> = new EventEmitter<TYPE.Document[]>();

  public fileList: TYPE.Document[] = [];
  public filteredFolders: TYPE.Folder[] = [];
  public filteredDocuments: TYPE.Document[] = [];
  public documentsCommanderForm: FormGroup = this.formBuilder.group({
    searchString: ['']
  });
  public currentFolder: TYPE.Folder | null = null;
  private directoryStack: StorageFilesystemResource[] = [];
  private folderEntryMap: Record<number, StorageFilesystemResource> = {};

  public ngOnInit(): void {
    this.documentsCommanderForm.controls['searchString'].valueChanges
      .pipe(startWith(this.documentsCommanderForm.controls['searchString'].value), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyLocalFilter());

    this.loadTemplateFolderContent();
  }

  public close(): void {
    this.visibleChange.emit(false);
  }

  public folderBack(): void {
    this.documentsCommanderForm.controls['searchString'].setValue('', {emitEvent: false});

    if (this.directoryStack.length === 0) {
      this.loadTemplateFolderContent();
      return;
    }

    this.directoryStack = this.directoryStack.slice(0, -1);
    this.loadTemplateFolderContent();
  }

  public selectFolder(folder: TYPE.Folder): void {
    if (!folder.name) {
      return;
    }

    const currentFolderEntry = this.folderEntryMap[folder.id];
    if (!currentFolderEntry) {
      return;
    }

    this.directoryStack.push({
      id: String(currentFolderEntry.id),
      name: currentFolderEntry.name,
      type: 'directory',
      deletable: false
    });
    this.documentsCommanderForm.controls['searchString'].setValue('', {emitEvent: false});
    this.loadTemplateFolderContent();
  }

  private loadTemplateFolderContent(): void {
    const currentDir = this.directoryStack.at(-1);
    const storageModule: StorageModule | undefined = this.getTemplateStorageModule();
    const request$ = this.api.getSettingsTemplatesByDirectory(currentDir?.id ?? '', storageModule);

    request$.pipe(take(1)).subscribe({
      next: (response: StorageFilesystemGet200Response) => {
        const entries: StorageFilesystemResource[] = response.contents;
        if (!currentDir && this.templateModule && !storageModule) {
          this.folderEntryMap = {};
          this.currentFolder = {
            id: 0,
            name: this.languageService.getTranslationByKey('DOCUMENTSMYFILES'),
            parent_id: 0,
            count: 0,
            documents: [],
            folders: []
          };
          this.fileList = [];
          return;
        }

        const storageModules: StorageModule[] = Object.values(StorageModule);
        const allowedDirectories = currentDir
          ? entries
          : this.templateModule
            ? entries.filter((entry) => entry.type === 'directory')
            : entries.filter((entry) => entry.type === 'directory' && storageModules.includes(entry.id as StorageModule));
        const directoryEntries = allowedDirectories.filter((entry) => entry.type === 'directory');
        this.folderEntryMap = directoryEntries.reduce((map, entry, index) => {
          map[index] = entry;
          return map;
        }, {} as Record<number, StorageFilesystemResource>);
        const documents = currentDir
          ? entries
            .filter((entry) => entry.type === 'file' && !!entry.file)
            .map((entry) => storageEntryToDocument({
              file: entry.file!,
              fallback: entry.fallback,
              could_be_fallback: entry.could_be_fallback
            }))
          : [];

        this.currentFolder = {
          id: this.directoryStack.length,
          name: this.directoryStack.length > 0
            ? this.resolveDirectoryName(this.directoryStack.at(-1) as StorageFilesystemResource)
            : this.languageService.getTranslationByKey('DOCUMENTSMYFILES'),
          parent_id: this.directoryStack.length > 1 ? this.directoryStack.length - 1 : 0,
          count: entries.length,
          documents,
          folders: directoryEntries
            .map((entry, index) => ({
              id: index,
              name: this.resolveDirectoryName(entry),
              parent_id: this.directoryStack.length,
              count: entry.file_count || 0,
              documents: [],
              folders: []
            }))
        };

        this.fileList = this.currentFolder.documents ?? [];
        this.applyLocalFilter();
      },
      error: (error) => {
        this.folderEntryMap = {};
        console.error('Failed to load template folder content', error);
        this.toaster.error(
          this.languageService.getTranslationByKey('TEMPLATELOADERRORTITLE'),
          this.languageService.getTranslationByKey('TEMPLATELOADERRORDESCRIPTION')
        );
      }
    });
  }

  private resolveDirectoryName(entry: StorageFilesystemResource): string {
    return entry.name?.trim() || entry.id;
  }

  private applyLocalFilter(): void {
    const searchString = String(this.documentsCommanderForm.controls['searchString'].value ?? '').trim().toLowerCase();
    const folders = this.currentFolder?.folders ?? [];
    const documents = this.currentFolder?.documents ?? [];

    if (!searchString) {
      this.filteredFolders = folders;
      this.filteredDocuments = documents;
      return;
    }

    this.filteredFolders = folders.filter((folder) => (folder.name || '').toLowerCase().includes(searchString));
    this.filteredDocuments = documents.filter((document) => (document.filename || '').toLowerCase().includes(searchString));
  }

  public update(): void {
    this.filesChange.emit(this.files);
  }

  public canGoBack(): boolean {
    return this.directoryStack.length > 0;
  }

  public hasSearchQuery(): boolean {
    return String(this.documentsCommanderForm.controls['searchString'].value ?? '').trim().length > 0;
  }

  private getTemplateStorageModule(): StorageModule | undefined {
    if (!this.templateModule) {
      return undefined;
    }
    const storageModules: StorageModule[] = Object.values(StorageModule);
    return storageModules.includes(this.templateModule as StorageModule) ? this.templateModule as StorageModule : undefined;
  }
}
